import React from 'react';
import PropTypes from 'prop-types';

/* eslint-disable jsx-a11y/anchor-is-valid, jsx-a11y/interactive-supports-focus */

/**
 * Pagination component following Kinetic Design System
 * Renders page numbers with ellipsis and prev/next navigation
 */
const Pagination = ({ currentPage, totalPages, onPageChange, isDisabled=false }) => {
	// Generate page numbers to display
	const getPageNumbers = () => {
		const pages = [];
		const maxPagesToShow = 5; // Show max 5 page numbers at a time
		
		if (totalPages <= maxPagesToShow + 2) {
			// Show all pages if total is small
			for (let i = 1; i <= totalPages; i++) {
				pages.push(i);
			}
		} else {
			// Always show first page
			pages.push(1);
			
			// Calculate range around current page
			let startPage = Math.max(2, currentPage - 1);
			let endPage = Math.min(totalPages - 1, currentPage + 1);
			
			// Adjust range if at beginning
			if (currentPage <= 3) {
				startPage = 2;
				endPage = Math.min(4, totalPages - 1);
			}
			
			// Adjust range if at end
			if (currentPage >= totalPages - 2) {
				startPage = Math.max(2, totalPages - 3);
				endPage = totalPages - 1;
			}
			
			// Add ellipsis before if needed
			if (startPage > 2) {
				pages.push('ellipsis-start');
			}
			
			// Add middle pages
			for (let i = startPage; i <= endPage; i++) {
				pages.push(i);
			}
			
			// Add ellipsis after if needed
			if (endPage < totalPages - 1) {
				pages.push('ellipsis-end');
			}
			
			// Always show last page
			pages.push(totalPages);
		}
		
		return pages;
	};

	const pageNumbers = getPageNumbers();

	const handlePageClick = (page) => {
		if (page !== currentPage && typeof page === 'number') {
			onPageChange(page);
		}
	};

	const handlePrevious = () => {
		if (currentPage > 1) {
			onPageChange(currentPage - 1);
		}
	};

	const handleNext = () => {
		if (currentPage < totalPages) {
			onPageChange(currentPage + 1);
		}
	};

	return (
		<nav aria-label="Page navigation">
			<ul className="pagination">
				{/* Previous button */}

				<li className={`pagination-previous ${currentPage === 1 || isDisabled ? 'disabled' : ''}`}>
					<a
						aria-label="Go to previous page"
						role="button"
						onClick={(e) => {
							e.preventDefault();
							handlePrevious();
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								handlePrevious();
							}
						}}
						tabIndex={currentPage === 1 ? -1 : 0}
					>
						<i className="ddc-icon ddc-icon-skinny-arrow-left" aria-hidden="true" />
					</a>
				</li>

				{/* Page numbers */}
				{pageNumbers.map((page) => {
					if (typeof page === 'string' && page.startsWith('ellipsis')) {
						return (
							<li key={page} className="pagination-ellipsis">
								<span>&hellip;</span>
							</li>
						);
					}

					return (
						<li 
							key={`page-${page}`} 
							className={page === currentPage ? 'active' : ''}
						>
							<a
								aria-label={`Go to page ${page}`}
								role="button"
								aria-current={page === currentPage ? 'page' : undefined}
								onClick={(e) => {
									e.preventDefault();
									if (!isDisabled) handlePageClick(page);
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										if (!isDisabled) handlePageClick(page);
									}
								}}
							>
								{page}
							</a>
						</li>
					);
				})}

				{/* Next button */}
				<li className={`pagination-next ${currentPage === totalPages ? 'disabled' : ''}`}>
					<a 
						aria-label="Go to next page"
						role="button"
						onClick={(e) => {
							e.preventDefault();
							handleNext();
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								handleNext();
							}
						}}
						tabIndex={currentPage === totalPages ? -1 : 0}
					>
						<i className="ddc-icon ddc-icon-skinny-arrow-right" aria-hidden="true" />
					</a>
				</li>
			</ul>
		</nav>
	);
};

Pagination.propTypes = {
	currentPage: PropTypes.number.isRequired,
	totalPages: PropTypes.number.isRequired,
	onPageChange: PropTypes.func.isRequired,
	isDisabled: PropTypes.bool
};

export default Pagination;